import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { listAccounts } from '../api/admin'
import type { AdminAccount, AdminPagination } from '../api/adminTypes'
import { Card } from '../components/ui/Card'
import { StatusBadge } from './StatusBadge'
import { formatDateTime } from './format'
import { LoadingNote, ErrorNote, EmptyNote } from './Notes'
import { Pager } from './Pager'

export function AccountsPage() {
  const [accounts, setAccounts] = useState<AdminAccount[] | null>(null)
  const [pagination, setPagination] = useState<AdminPagination | null>(null)
  const [offset, setOffset] = useState(0)
  const [error, setError] = useState(false)

  useEffect(() => {
    setError(false)
    listAccounts(offset)
      .then((res) => {
        setAccounts(res.accounts)
        setPagination(res.pagination)
      })
      .catch(() => setError(true))
  }, [offset])

  return (
    <div>
      <h1
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title-lg)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-6) 0',
        }}
      >
        Accounts
      </h1>

      {error && <ErrorNote>Something went wrong loading accounts.</ErrorNote>}
      {accounts === null && !error && <LoadingNote>Loading accounts…</LoadingNote>}
      {accounts !== null && accounts.length === 0 && <EmptyNote>No accounts yet.</EmptyNote>}

      {accounts !== null && accounts.length > 0 && (
        <>
          <Card padding={false} style={{ overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-body)' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-outline-variant)' }}>
                  <Th>Email</Th>
                  <Th>Status</Th>
                  <Th>Drafts</Th>
                  <Th>Submissions</Th>
                  <Th>Last login</Th>
                  <Th>Created</Th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--color-outline-subtle)' }}>
                    <Td>
                      <Link
                        to={`/accounts/${a.id}`}
                        style={{ color: 'var(--color-action-primary)', textDecoration: 'none', fontWeight: 'var(--weight-semibold)' }}
                      >
                        {a.email}
                      </Link>
                    </Td>
                    <Td><StatusBadge status={a.status} /></Td>
                    <Td>{a.draft_count ?? 0}</Td>
                    <Td>{a.submission_count ?? 0}</Td>
                    <Td>{formatDateTime(a.last_login_at)}</Td>
                    <Td>{formatDateTime(a.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          {pagination && <Pager pagination={pagination} onOffsetChange={setOffset} />}
        </>
      )}
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: '10px 16px',
        fontSize: 'var(--text-label-sm)',
        fontWeight: 'var(--weight-semibold)',
        color: 'var(--color-text-secondary)',
      }}
    >
      {children}
    </th>
  )
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: '12px 16px', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-primary)' }}>
      {children}
    </td>
  )
}
